import React from 'react';
import { formatHijriDate } from '../../services/islamicOccasionsService';

/**
 * Displays today's Islamic occasion (if any) on the homepage.
 * Styled by occasion type (joyous/mourning/significant), inheriting center theme colors.
 */
const IslamicOccasionBanner = ({ todayData, theme, isMobile }) => {
  if (!todayData || !todayData.occasions || todayData.occasions.length === 0) return null;

  const primary = theme?.colors?.primary || '#1A5632';
  const headingColor = theme?.colors?.headingColor || primary;
  const isDark = theme?.isDarkBackground;

  // Occasion type → style mapping (derived from theme, not hardcoded)
  const typeStyles = {
    joyous: {
      background: isDark
        ? `linear-gradient(135deg, rgba(34,197,94,0.15) 0%, rgba(234,179,8,0.1) 100%)`
        : `linear-gradient(135deg, #f0fdf4 0%, #fefce8 100%)`,
      border: isDark ? '1px solid rgba(34,197,94,0.3)' : '1px solid #bbf7d0',
      borderLeft: `4px solid ${primary}`,
      badgeBg: isDark ? 'rgba(34,197,94,0.2)' : '#dcfce7',
      badgeColor: isDark ? '#86efac' : '#166534',
      titleColor: isDark ? '#86efac' : '#14532d',
      textColor: isDark ? '#d1d5db' : '#475569',
      btnBg: primary,
      btnColor: '#fff',
      emoji: '🎉',
      label: 'Joyous Occasion',
    },
    mourning: {
      background: isDark
        ? `linear-gradient(135deg, #111 0%, #1a1a1a 100%)`
        : `linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)`,
      border: '1px solid #333',
      borderLeft: '4px solid #555',
      badgeBg: '#333',
      badgeColor: '#aaa',
      titleColor: '#f0f0f0',
      textColor: '#aaa',
      btnBg: '#444',
      btnColor: '#ddd',
      emoji: '🏴',
      label: 'Mourning',
    },
    significant: {
      background: isDark
        ? `linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(139,92,246,0.08) 100%)`
        : `linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%)`,
      border: isDark ? '1px solid rgba(59,130,246,0.3)' : '1px solid #bfdbfe',
      borderLeft: '4px solid #3b82f6',
      badgeBg: isDark ? 'rgba(59,130,246,0.2)' : '#dbeafe',
      badgeColor: isDark ? '#93c5fd' : '#1e40af',
      titleColor: isDark ? '#93c5fd' : '#1e3a5f',
      textColor: isDark ? '#d1d5db' : '#475569',
      btnBg: '#1e40af',
      btnColor: '#fff',
      emoji: '✨',
      label: 'Significant Occasion',
    },
  };

  // Render the first (primary) occasion. If multiple on same day, show first.
  const occasion = todayData.occasions[0];
  const style = typeStyles[occasion.type] || typeStyles.significant;
  const hijriStr = formatHijriDate(todayData.hijri, 'full');

  return (
    <div style={{
      borderRadius: '12px',
      padding: isMobile ? '16px' : '20px 24px',
      marginBottom: isMobile ? '20px' : '28px',
      position: 'relative',
      overflow: 'hidden',
      background: style.background,
      border: style.border,
      borderLeft: style.borderLeft,
    }}>
      {/* Hijri date badge (top right) */}
      <span style={{
        position: 'absolute', top: isMobile ? '12px' : '16px', right: isMobile ? '12px' : '20px',
        fontSize: '12px', fontWeight: 500, color: style.textColor, opacity: 0.7,
      }}>
        {hijriStr}
      </span>

      {/* Type badge */}
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
        fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
        marginBottom: '10px',
        background: style.badgeBg, color: style.badgeColor,
      }}>
        {style.emoji} {style.label}
      </div>

      {/* Title */}
      <div style={{
        fontSize: isMobile ? '18px' : '20px', fontWeight: 600,
        color: style.titleColor, marginBottom: '4px',
        fontFamily: theme?.fonts?.heading || 'inherit',
      }}>
        {occasion.nameEn}
      </div>

      {/* Arabic name */}
      {occasion.nameAr && (
        <div style={{
          fontSize: isMobile ? '16px' : '18px', opacity: 0.8,
          direction: 'rtl', marginBottom: '8px',
          fontFamily: "'Traditional Arabic', 'Noto Naskh Arabic', serif",
          color: occasion.type === 'mourning' ? '#ccc' : style.textColor,
        }}>
          {occasion.nameAr}
        </div>
      )}

      {/* Significance + Amaal summary */}
      <div style={{
        fontSize: '14px', color: style.textColor,
        lineHeight: 1.5, marginBottom: '12px',
      }}>
        {occasion.significance}
        {occasion.amaalSummary && (
          <>
            <br />
            <strong>Recommended: </strong>{occasion.amaalSummary}
          </>
        )}
      </div>

      {/* Action links */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {occasion.amaalUrlPrimary && (
          <a
            href={occasion.amaalUrlPrimary}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', borderRadius: '8px', fontSize: '13px',
              fontWeight: 500, textDecoration: 'none',
              background: style.btnBg, color: style.btnColor,
              transition: 'opacity 0.2s',
            }}
          >
            🤲 View Amaal on Duas.org →
          </a>
        )}
        {occasion.amaalUrlSecondary && (
          <a
            href={occasion.amaalUrlSecondary}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', borderRadius: '8px', fontSize: '13px',
              fontWeight: 500, textDecoration: 'none',
              background: 'transparent',
              border: `1px solid ${style.btnBg}`,
              color: occasion.type === 'mourning' ? '#aaa' : style.btnBg,
              transition: 'opacity 0.2s',
            }}
          >
            🎧 DuaPlayer →
          </a>
        )}
      </div>

      {/* Show additional occasions if multiple on same day */}
      {todayData.occasions.length > 1 && (
        <div style={{
          marginTop: '12px', paddingTop: '12px',
          borderTop: `1px solid ${occasion.type === 'mourning' ? '#333' : '#e2e8f0'}`,
          fontSize: '13px', color: style.textColor,
        }}>
          Also today: {todayData.occasions.slice(1).map(o => o.nameEn).join(' • ')}
        </div>
      )}
    </div>
  );
};

export default IslamicOccasionBanner;
